import type { ScoreBreakdown } from '@nt2/engine';
import { audio } from '../audio/engine.js';
import { announce, clamp, el, on, setText } from './dom.js';

/**
 * The end-of-match reckoning.
 *
 * Adding up in silence tells a player their score; adding up a line at a time
 * tells them *why*, which is the only way the scoring rule gets learnt. So
 * each word arrives with its own points, the crossings are called out where
 * they happen, and the letters nobody used come off at the end in front of
 * everybody.
 *
 * It is an animation over a table that is already complete and readable, not
 * a thing you have to sit through: the final figures are in the DOM from the
 * first frame — only their visibility is animated — so a tap, the keyboard,
 * or `prefers-reduced-motion` shows the finished sum immediately.
 */

export interface ReckoningRow {
  seatId: string;
  name: string;
  colour: string;
  you: boolean;
  wentOut: boolean;
  breakdown: ScoreBreakdown;
}

export interface Reckoning {
  root: HTMLElement;
  /** Show the finished sum at once. Safe to call repeatedly. */
  skip(): void;
  /** Cancel any pending steps, for a dialog that closes early. */
  stop(): void;
}

const reduced = (): boolean =>
  matchMedia('(prefers-reduced-motion: reduce)').matches;

/** One revealed line of the sum. */
interface Step {
  node: HTMLElement;
  /** What the running total becomes once this line is showing. */
  running: number;
  /** Cents of detune, so the tally rises as it goes. */
  detune: number;
  sound: 'tick' | 'bad' | 'win';
}

export function reckoning(row: ReckoningRow): Reckoning {
  const b = row.breakdown;
  const root = el('div', { class: 'reck' });

  const total = el('b', { class: 'reck__total', text: '0' });
  const head = el('div', { class: 'reck__head' },
    el('span', {},
      el('b', { text: row.name + (row.you ? ' (you)' : '') }),
      el('div', { class: 'setting__hint', text: summary(b, row.wentOut) })),
    total);
  const dot = el('span', { class: 'reck__dot' });
  dot.style.background = row.colour;
  head.prepend(dot);
  root.append(head);

  const lines = el('div', { class: 'reck__lines' });
  root.append(lines);

  const steps: Step[] = [];
  let running = 0;

  const add = (
    label: string, detail: string, points: number,
    kind: 'word' | 'dud' | 'penalty' | 'bonus',
  ): void => {
    running += points;
    const node = el('div', { class: `reck__line reck__line--${kind}` },
      el('span', { class: 'reck__word', text: label }),
      el('span', { class: 'reck__detail setting__hint', text: detail }),
      el('span', {
        class: 'reck__pts',
        text: points > 0 ? `+${points}` : String(points),
      }));
    node.classList.add('reck__line--pending');
    lines.append(node);
    steps.push({
      node,
      running,
      detune: clamp(steps.length * 70, 0, 1400),
      sound: kind === 'penalty' ? 'bad' : kind === 'bonus' ? 'win' : 'tick',
    });
  };

  // Words in the order they read: across before down, top-left first. A
  // crossing letter turns up in two of these lines, which is the point.
  const words = [...b.words].sort((x, y) =>
    x.orientation.localeCompare(y.orientation) ||
    x.start.y - y.start.y || x.start.x - y.start.x);

  for (const w of words.filter((x) => x.valid)) {
    add(title(w.word), w.orientation === 'across' ? 'across' : 'down',
      w.points, 'word');
  }

  const duds = words.filter((x) => !x.valid);
  if (duds.length) {
    add(duds.length === 1 ? title(duds[0]!.word) : `${duds.length} rejected`,
      duds.length === 1 ? 'not in the word list' : 'not in the word list',
      0, 'dud');
  }

  if (b.penalty > 0) {
    const held = b.unused.filter((u) => u.where === 'rack').length;
    const stranded = b.unused.length - held;
    const parts: string[] = [];
    if (held) parts.push(`${held} in hand`);
    if (stranded) parts.push(`${stranded} in no word`);
    add(`${b.unused.length} letter${b.unused.length === 1 ? '' : 's'} unused`,
      parts.join(', '), -b.penalty, 'penalty');
  }

  if (b.bonus > 0) {
    add('Used every letter', 'bonus for going out', b.bonus, 'bonus');
  }

  if (!steps.length) {
    lines.append(el('div', { class: 'setting__hint', text: 'Nothing was played.' }));
  }

  let timer = 0;
  let at = 0;
  let done = false;

  const settle = (): void => {
    done = true;
    window.clearTimeout(timer);
    for (const s of steps) s.node.classList.remove('reck__line--pending');
    setText(total, String(b.total));
    total.classList.toggle('reck__total--bad', b.total < 0);
    root.classList.add('reck--done');
  };

  const step = (): void => {
    const s = steps[at++];
    if (!s) { settle(); return; }
    s.node.classList.remove('reck__line--pending');
    setText(total, String(s.running));
    total.classList.toggle('reck__total--bad', s.running < 0);
    if (row.you) audio.play(s.sound, { detune: s.detune, throttleMs: 0 });
    // Long grids must not turn this into a wait, so the gap shrinks with the
    // number of lines rather than the total time growing with it.
    timer = window.setTimeout(step, clamp(3200 / steps.length, 110, 420));
  };

  // A tap anywhere on the sum finishes it. That is the obvious gesture for
  // "get on with it", and it means the animation never traps anybody.
  on(root, 'pointerdown', () => { if (!done) settle(); });

  if (reduced() || !steps.length) {
    settle();
  } else {
    setText(total, '0');
    timer = window.setTimeout(step, 420);
  }

  if (row.you) {
    announce(`Your score: ${b.total}. ${summary(b, row.wentOut)}`);
  }

  return {
    root,
    skip: () => { if (!done) settle(); },
    stop: () => { window.clearTimeout(timer); },
  };
}

/** One line of prose for what the total is made of. */
function summary(b: ScoreBreakdown, wentOut: boolean): string {
  const good = b.words.filter((w) => w.valid).length;
  const bits = [`${good} word${good === 1 ? '' : 's'} worth ${b.wordPoints}`];
  if (b.penalty > 0) bits.push(`${b.unused.length} letters unused, −${b.penalty}`);
  if (wentOut && b.bonus > 0) bits.push(`+${b.bonus} for going out`);
  return bits.join(' · ');
}

const title = (w: string): string => w.charAt(0) + w.slice(1).toLowerCase();
